<template>
  <div class="input-wrapper" :class="inputWrapperClasses">
    <label v-if="label.length" class="label" :for="id" @click="onClickLabel">{{ label }}</label>

    <vue-tel-input
      ref="tel"
      :model-value="phone"
      :auto-format="false"
      :auto-default-country="false"
      :input-options="inputOptions"
      :dropdown-options="dropdownOptions"
      :preferred-countries="preferredCountries"
      :default-country="defaultCountry"
      :ignored-countries="ignoredCountries"
      :mode="mode"
      :only-countries="onlyCountries"
      valid-characters-only
      :disabled="disabled"
      class="input"
      @on-input="input"
      @country-changed="input()"
    >
      <template #arrow-icon>
        <VIconSvg name="UnfoldMore" size="xs" color="fc-subduet" class="arrow-icon" />
      </template>
    </vue-tel-input>

    <VErrorTooltip v-if="error" :error="error" />
  </div>
</template>

<script setup>
import { VueTelInput } from "vue-tel-input";
VueTelInput.compatConfig = { MODE: 3 };

import { VIconSvg } from "@/components";
import VErrorTooltip from "../VErrorTooltip/VErrorTooltip.vue";
import ValidationService from "@/services/validation.service";
import { defineProps, ref, computed, watch, defineEmits } from "vue";
import { getRandomId } from "@/mixins/global.mixin";

const { INTEGER_REG_EXP } = ValidationService;

const emit = defineEmits(["input"]);

const tel = ref();

const props = defineProps({
  value: {
    type: String,
    default: "",
  },

  label: {
    type: String,
    default: "",
  },

  size: {
    type: String,
    default: "md",
  },

  placeholder: {
    type: String,
    default: "",
  },

  preferredCountries: {
    type: Array,
    default: () => ["US"],
  },

  defaultCountry: {
    type: String,
    default: "US",
  },

  maxLength: {
    type: Number,
    default: 13,
  },

  mode: {
    type: String,
    default: "national",
  },

  onlyCountries: {
    type: Array,
    default: () => [],
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  error: {
    type: String,
    default: "",
  },

  variant: {
    type: String,
    default: "regular",
  },

  /**
   * Set required.
   */
  required: {
    type: Boolean,
    default: false,
  },
});

const phone = ref("");

const id = computed(() => getRandomId());
const ignoredCountries = ["RU", "BY"];

const inputOptions = computed(() => {
  return {
    id: id,
    type: "tel",
    placeholder: props.placeholder,
    maxlength: props.maxLength,
    showDialCode: false,
    autocomplete: "off",
  };
});

const dropdownOptions = computed(() => {
  return {
    showFlags: true,
    showDialCodeInList: true,
    showDialCodeInSelection: true,
  };
});

const inputWrapperClasses = computed(() => {
  const variant = `input-${props.variant}`;
  const size = `input-${props.variant}-size-${props.size}`;
  const classes = {
    "input-error": props.error,
    "input-required": props.required,
  };

  return [variant, size, classes];
});

const onChangeValue = (value) => {
  const isNotEmptyPhone = !!phone.value.length;

  phone.value = value;

  if (isNotEmptyPhone) {
    const { phoneObject } = tel.value;
    const { nationalNumber } = phoneObject;

    if (nationalNumber) {
      tel.value.emitInput(nationalNumber.toString(), phoneObject);
      phone.value = nationalNumber.toString();
    }
  }
};

watch(() => props.value, onChangeValue, {
  immediate: true,
});

const input = (value = "") => {
  const { phoneObject } = tel.value;

  const isNotEmptyPhoneObject = !!Object.keys(phoneObject).length;

  if (value) {
    phone.value = transformValue(value, INTEGER_REG_EXP);
    tel.value.phone = transformValue(value, INTEGER_REG_EXP);
  }

  if (isNotEmptyPhoneObject) {
    const { nationalNumber } = phoneObject;

    if (nationalNumber) {
      tel.value.phone = nationalNumber.toString();
    }

    let { number } = tel.value.phoneObject;

    if (number !== props.value) {
      if (typeof number === "undefined") number = "";

      emit("input", number);
    }
  }
};

watch(
  () => tel.value?.activeCountryCode,
  () => {
    if (!tel.value.activeCountryCode) {
      tel.value.activeCountryCode = props.defaultCountry;
    }
  }
);

const transformValue = (value, exp) => {
  const regExp = new RegExp(exp, "ig");
  const matches = String(value).match(regExp);

  return matches ? matches.join("") : "";
};

const onClickLabel = () => {
  tel.value.focus();
};
</script>

<style lang="css" scoped>
@import "~vue-tel-input/vue-tel-input.css";
</style>

<style lang="postcss" scoped>
.input-wrapper {
  @apply relative;

  .label {
    @apply text-sm font-bold text-fc-subduet;
    @apply mb-1.5 block;
  }

  .input:deep() {
    @apply rounded-4xl border border-solid border-fc-stroke;
    @apply shadow-none;

    .arrow-icon {
      @apply ml-1;
    }

    .vti__dropdown {
      @apply rounded-l-4xl;
      @apply bg-fc-back;

      .vti__selection {
        @apply text-sm;
        @apply select-none;

        .vti__country-code {
          @apply text-fc-ink;
        }
      }
    }

    .vti__input {
      @apply rounded-r-4xl;
      @apply text-fc-ink;

      &:focus-within {
        @apply shadow-none ring-0;
        @apply !bg-white;
        @apply transition-colors duration-300;
      }

      &:hover {
        @apply bg-blaze-orange bg-opacity-5;
        @apply transition-colors duration-300;
      }

      &::placeholder {
        @apply text-oslo-gray;
        @apply select-none;
      }
    }

    .vti__dropdown-list {
      @apply rounded-lg border border-solid border-fc-stroke shadow-outer;
      @apply w-fit max-w-xs sm:max-w-max md:max-w-sm lg:max-w-max;
      @apply top-full h-fit;
      @apply z-10 text-fc-ink;

      &:focus-visible {
        @apply outline-none;
      }
    }

    .vti__dropdown-item {
      @apply px-4 py-2.5;
      @apply whitespace-nowrap;

      strong,
      span {
        @apply font-normal;
      }

      &:hover {
        @apply bg-gray-100;
      }

      &:active {
        @apply bg-gray-200 font-medium;

        strong {
          @apply bg-gray-200 font-medium;
        }
      }

      &.last-preferred {
        @apply border-gray-300;
      }
    }

    &:focus-within {
      @apply border border-solid border-fc-accent;
      @apply shadow-none;
    }
  }
}

.input-regular {
  &-size-md {
    .input:deep() {
      .vti__input {
        @apply px-[0.6rem] py-[0.688rem];
        @apply text-sm;
      }
    }
  }
}

.input-inner {
  .input:deep() {
    @apply border-opacity-20 bg-white bg-opacity-10;

    .arrow-icon {
      svg {
        path,
        circle {
          @apply text-white/40;
        }
      }

      &:hover,
      &:focus-within {
        svg {
          path,
          circle {
            @apply text-white/80;
          }
        }
      }
    }

    .vti__dropdown {
      @apply bg-transparent;

      .vti__country-code {
        @apply !text-white/70;
      }
    }

    .vti__input {
      @apply bg-transparent text-white;

      &:focus-within {
        @apply border-none !bg-opacity-15;
      }
    }
  }

  &-size-md {
    .input:deep() {
      .vti__input {
        @apply px-[0.6rem] py-[0.688rem];
        @apply text-sm;
      }
    }
  }

  &-size-lg {
    .input:deep() {
      .vti__input {
        @apply p-[1.219rem];
        @apply text-base;
      }
    }
  }
}

.input-required {
  &.input-regular {
    .label {
      &:after {
        @apply ml-1 mt-1.5 content-['*'];
        @apply ml-1 font-medium text-fc-error;
        @apply z-[1];
      }
    }
  }

  &.input-inner {
    &:before {
      @apply ml-1 mt-1.5 content-['*'];
      @apply text-xs;
      @apply mr-1 font-medium text-fc-error;
      @apply absolute;
      @apply z-[1];
    }

    &-size-md {
      &:before {
        @apply -top-[0.1rem] left-[0.463rem];
      }
    }

    &-size-lg {
      &:before {
        @apply left-[0.563rem] top-[0.375rem];
      }
    }
  }
}

.input-error {
  .input:deep() {
    @apply !border-fc-error;

    .vti__input {
      &:hover,
      &:focus {
        @apply bg-fc-error-back text-fc-error;
      }
    }
  }

  &.input-inner {
    .input:deep() {
      .vti__input {
        &:hover,
        &:focus {
          @apply bg-inherit text-inherit;
        }
      }
    }
  }
}
</style>
